import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Alert,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';

export function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // CSS keyframes for animations
  const keyframes = {
    '@keyframes pulse': {
      '0%, 100%': {
        transform: 'scale(1)',
        opacity: 1,
      },
      '50%': {
        transform: 'scale(1.1)',
        opacity: 0.8,
      },
    },
    '@keyframes float': {
      '0%, 100%': {
        transform: 'translateY(0px)',
      },
      '50%': {
        transform: 'translateY(-10px)',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await signUp(email, password);
      navigate('/login');
    } catch (error) {
      setError('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#0a0a0a',
      ...keyframes
    }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: 1,
          borderColor: '#1a1a1a',
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(10, 10, 10, 0.9)'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img
                src="/praytell_logo.png"
                alt="mycatalyze"
                style={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)' }}
              />
              <Typography variant="h6" component="h1" fontWeight="bold" sx={{ color: 'white' }}>
                MyCatalyze
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Box
        component="section"
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 8, md: 16 },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                textAlign: { xs: 'center', md: 'left' },
                mb: { xs: 4, md: 0 }
              }}>
                <Typography 
                  variant="h1" 
                  component="h1" 
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    fontWeight: 800,
                    color: 'white',
                    mb: 3,
                    lineHeight: 1.1,
                    background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  A world where knowledge compounds
                </Typography>
                
                <Typography 
                  variant="h5" 
                  component="p" 
                  sx={{
                    color: '#b0b0b0',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    mb: 4,
                    maxWidth: { xs: '100%', md: '500px' }
                  }}
                >
                  Every message is labeled by meaning, AI proposes next steps, and teams operate from live graphs of people, projects, and commitments.
                </Typography>

                {/* Abstract Data Visualization - Only show on desktop */}
                {!isMobile && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: '500px',
                      height: '300px',
                      background: 'radial-gradient(circle at center, rgba(58, 58, 58, 0.1) 0%, transparent 70%)',
                      borderRadius: '20px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Network nodes */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '20%',
                        left: '30%',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        bgcolor: '#4a9eff',
                        boxShadow: '0 0 20px rgba(74, 158, 255, 0.6)',
                        animation: 'pulse 3s ease-in-out infinite'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '60%',
                        left: '20%',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        bgcolor: '#ff6b6b',
                        boxShadow: '0 0 25px rgba(255, 107, 107, 0.6)',
                        animation: 'pulse 3s ease-in-out infinite 1s'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '40%',
                        right: '25%',
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        bgcolor: '#51cf66',
                        boxShadow: '0 0 22px rgba(81, 207, 102, 0.6)',
                        animation: 'pulse 3s ease-in-out infinite 2s'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: '30%',
                        left: '50%',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        bgcolor: '#ffd43b',
                        boxShadow: '0 0 28px rgba(255, 212, 59, 0.6)',
                        animation: 'pulse 3s ease-in-out infinite 0.5s'
                      }}
                    />

                    {/* Connection lines */}
                    <svg
                      width="100%"
                      height="100%"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1
                      }}
                    >
                      <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#4a9eff" stopOpacity="0.6" />
                          <stop offset="100%" stopColor="#ff6b6b" stopOpacity="0.6" />
                        </linearGradient>
                      </defs>
                      <line
                        x1="30%"
                        y1="20%"
                        x2="20%"
                        y2="60%"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        opacity="0.7"
                      />
                      <line
                        x1="20%"
                        y1="60%"
                        x2="50%"
                        y2="70%"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        opacity="0.7"
                      />
                      <line
                        x1="50%"
                        y1="70%"
                        x2="75%"
                        y2="40%"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        opacity="0.7"
                      />
                      <line
                        x1="75%"
                        y1="40%"
                        x2="30%"
                        y2="20%"
                        stroke="url(#lineGradient)"
                        strokeWidth="2"
                        opacity="0.7"
                      />
                    </svg>

                    {/* Floating data elements */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '15%',
                        right: '15%',
                        color: 'rgba(255, 255, 255, 0.1)',
                        fontSize: '1.5rem',
                        animation: 'float 6s ease-in-out infinite'
                      }}
                    >
                      📊
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: '20%',
                        right: '10%',
                        color: 'rgba(255, 255, 255, 0.08)',
                        fontSize: '1.2rem',
                        animation: 'float 8s ease-in-out infinite reverse'
                      }}
                    >
                      🔗
                    </Box>
                  </Box>
                )}

                {/* CTA Buttons - Only show on desktop */}
                {!isMobile && (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/register?plan=beta')}
                      startIcon={<ArrowForward />}
                      sx={{
                        bgcolor: '#3a3a3a',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        border: '1px solid #4a4a4a',
                        '&:hover': {
                          bgcolor: '#4a4a4a',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                        }
                      }}
                    >
                      Join the Beta
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/login')}
                      sx={{
                        borderColor: '#4a4a4a',
                        color: '#e0e0e0',
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        '&:hover': {
                          borderColor: '#5a5a5a',
                          bgcolor: 'rgba(74, 74, 74, 0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Sign In
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Right Column - Registration Form */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                width: '100%'
              }}>
                <Paper sx={{ 
                  p: 4, 
                  width: '100%', 
                  maxWidth: '450px',
                  bgcolor: '#2a2a2a',
                  border: '1px solid #3a3a3a',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}>
                  <Typography component="h2" variant="h4" align="center" gutterBottom sx={{ color: 'white', mb: 3 }}>
                    Create Your Account
                  </Typography>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#4a4a4a',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5a5a5a',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6a6a6a',
                          },
                          '& input': {
                            color: 'white',
                          },
                          '& label': {
                            color: '#b0b0b0',
                          },
                          '&.Mui-focused label': {
                            color: '#6a6a6a',
                          },
                        },
                      }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      id="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#4a4a4a',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5a5a5a',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6a6a6a',
                          },
                          '& input': {
                            color: 'white',
                          },
                          '& label': {
                            color: '#b0b0b0',
                          },
                          '&.Mui-focused label': {
                            color: '#6a6a6a',
                          },
                        },
                      }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#4a4a4a',
                          },
                          '&:hover fieldset': {
                            borderColor: '#5a5a5a',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6a6a6a',
                          },
                          '& input': {
                            color: 'white',
                          },
                          '& label': {
                            color: '#b0b0b0',
                          },
                          '&.Mui-focused label': {
                            color: '#6a6a6a',
                          },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{ 
                        mt: 3, 
                        mb: 2,
                        bgcolor: '#3a3a3a',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#4a4a4a',
                        },
                        '&:disabled': {
                          bgcolor: '#2a2a2a',
                          color: '#666',
                        }
                      }}
                    >
                      {loading ? 'Creating Account...' : 'Sign Up'}
                    </Button>
                    <Box sx={{ textAlign: 'center' }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/login')}
                        sx={{
                          color: '#5a5a5a',
                          '&:hover': {
                            color: '#7a7a7a',
                          }
                        }}
                      >
                        Already have an account? Sign In
                      </Link>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>


    </Box>
  );
} 